<template>
  <div class="client-detail-panel">
    <template v-if="!client">
      <div class="client-detail-panel__empty">
        <UserCircleIcon class="client-detail-panel__empty-icon" />
        <p class="client-detail-panel__empty-title">Sélectionnez un client</p>
        <p class="client-detail-panel__empty-text">
          Choisissez un client dans la liste pour afficher son profil, ses documents et son activité.
        </p>
      </div>
    </template>

    <template v-else>
      <div class="client-detail-panel__scroll">
        <!-- Profile card -->
        <section class="client-detail-card">
          <div class="client-detail-card__header">
            <UserCircleIcon class="client-detail-card__icon client-detail-card__icon--primary" />
            <h3 class="client-detail-card__title">Profil client</h3>
          </div>
          <div class="client-detail-card__body">
            <dl class="client-detail-card__grid">
              <div class="client-detail-card__row">
                <dt>Code client</dt>
                <dd>{{ client.login || client.username || '—' }}</dd>
              </div>
              <div class="client-detail-card__row">
                <dt>Email</dt>
                <dd>{{ client.email || '—' }}</dd>
              </div>
               <div class="client-detail-card__row">
                <dt>Contact</dt>
                <dd>{{ client.tel || '—' }}</dd>
              </div>
              <div v-if="!props.hideClientName" class="client-detail-card__row">
                <dt>Nom</dt>
                <dd>{{ [client.prenom, client.nom].filter(Boolean).join(' ') || '—' }}</dd>
              </div>
            </dl>
          </div>
        </section>

        <!-- Offer & status + documents completion -->
        <section class="client-detail-card">
          <div class="client-detail-card__header">
            <DocumentTextIcon class="client-detail-card__icon client-detail-card__icon--navy" />
            <h3 class="client-detail-card__title">Offre & statut</h3>
          </div>
          <div class="client-detail-card__body">
            <div class="client-detail-card__row">
              <dt>Offre</dt>
              <dd>
                <span class="client-detail-panel__badge client-detail-panel__badge--primary">
                  {{ docStatus?.offerName || '—' }}
                </span>
              </dd>
            </div>
            <div class="client-detail-card__row">
              <dt>Formulaire</dt>
              <dd>
                <TableBadge
                  :variant="formStatusVariant(docStatus?.formulaire_statut)"
                  :label="formStatusLabel(docStatus?.formulaire_statut)"
                />
              </dd>
            </div>
            <div class="client-detail-card__doc-grid">
              <span class="client-detail-card__doc-label">Photo</span>
              <span
                class="client-detail-panel__doc-dot"
                :class="!!docStatus?.photo ? 'client-detail-panel__doc-dot--ok' : 'client-detail-panel__doc-dot--missing'"
                :title="!!docStatus?.photo ? 'Déposé' : 'Manquant'"
              />
              <span class="client-detail-card__doc-label">ID</span>
              <span
                class="client-detail-panel__doc-dot"
                :class="!!docStatus?.justificatif_identite ? 'client-detail-panel__doc-dot--ok' : 'client-detail-panel__doc-dot--missing'"
                :title="!!docStatus?.justificatif_identite ? 'Déposé' : 'Manquant'"
              />
              <span class="client-detail-card__doc-label">Revenu</span>
              <span
                class="client-detail-panel__doc-dot"
                :class="!!docStatus?.justificatif_revenu ? 'client-detail-panel__doc-dot--ok' : 'client-detail-panel__doc-dot--missing'"
                :title="!!docStatus?.justificatif_revenu ? 'Déposé' : 'Manquant'"
              />
              <span class="client-detail-card__doc-label">Bancaire</span>
              <span
                class="client-detail-panel__doc-dot"
                :class="!!docStatus?.releve_bancaire ? 'client-detail-panel__doc-dot--ok' : 'client-detail-panel__doc-dot--missing'"
                :title="!!docStatus?.releve_bancaire ? 'Déposé' : 'Manquant'"
              />
              <span class="client-detail-card__doc-label">Résidence</span>
              <span
                class="client-detail-panel__doc-dot"
                :class="!!docStatus?.justificatif_residence ? 'client-detail-panel__doc-dot--ok' : 'client-detail-panel__doc-dot--missing'"
                :title="!!docStatus?.justificatif_residence ? 'Déposé' : 'Manquant'"
              />
            </div>
          </div>
        </section>

        <!-- Recent activity / logs -->
        <section class="client-detail-card">
          <div class="client-detail-card__header">
            <ClockIcon class="client-detail-card__icon client-detail-card__icon--gold" />
            <h3 class="client-detail-card__title">Activité récente</h3>
            <button
              v-if="recentLogs.length > 0"
              type="button"
              class="client-detail-card__link"
              @click="$emit('view-all-logs')"
            >
              Voir tout
            </button>
          </div>
          <div class="client-detail-card__body">
            <ul v-if="recentLogs.length > 0" class="client-detail-panel__log-list">
              <li
                v-for="log in recentLogs.slice(0, 5)"
                :key="log.id"
                class="client-detail-panel__log-item"
              >
                <span class="client-detail-panel__log-subject">{{ log.subject || log.sujet || '—' }}</span>
                <span class="client-detail-panel__log-date">{{ formatDate(log.created_at) }}</span>
              </li>
            </ul>
            <p v-else class="client-detail-panel__muted">Aucune activité récente.</p>
          </div>
        </section>

        <!-- Documents & signatures -->
        <section class="client-detail-card">
          <div class="client-detail-card__header">
            <PencilSquareIcon class="client-detail-card__icon client-detail-card__icon--success" />
            <h3 class="client-detail-card__title">Documents & signatures</h3>
            <button
              v-if="signatures.length > 0"
              type="button"
              class="client-detail-card__link"
              @click="$emit('view-all-signatures')"
            >
              Voir tout
            </button>
          </div>
          <div class="client-detail-card__body">
            <ul v-if="signatures.length > 0" class="client-detail-panel__sig-list">
              <li
                v-for="sig in signatures.slice(0, 5)"
                :key="sig.id"
                class="client-detail-panel__sig-item"
              >
                <span class="client-detail-panel__sig-doc">{{ sig.document_name || sig.document_type || '—' }}</span>
                <TableBadge
                  :variant="sig.statut === 'signe' ? 'signe' : 'en_attente'"
                />
                <span v-if="sig.signed_at" class="client-detail-panel__sig-date">{{ formatDate(sig.signed_at) }}</span>
              </li>
            </ul>
            <p v-else class="client-detail-panel__muted">Aucun document signé.</p>
          </div>
        </section>
      </div>
    </template>
  </div>
</template>

<script setup>
import {
  UserCircleIcon,
  DocumentTextIcon,
  ClockIcon,
  PencilSquareIcon,
} from '@heroicons/vue/24/outline';
import TableBadge from '@/components/dashboard/TableBadge.vue';

const props = defineProps({
  client: { type: Object, default: null },
  docStatus: { type: Object, default: null },
  recentLogs: { type: Array, default: () => [] },
  signatures: { type: Array, default: () => [] },
  hideClientName: { type: Boolean, default: false },
});

defineEmits(['view-all-logs', 'view-all-signatures']);

function formStatusLabel(statut) {
  const map = { non_commence: 'Non commencé', entame: 'Entamé', termine: 'Terminé', erreur: 'Erreur' };
  return map[statut] || 'Non commencé';
}

function formStatusVariant(statut) {
  const map = {
    non_commence: 'non_commence',
    entame: 'en_attente',
    termine: 'termine',
    erreur: 'erreur',
  };
  return map[statut] || 'non_commence';
}

function formatDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return val;
  }
}
</script>


<style scoped>
.client-detail-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: var(--font-primary);
  color: rgba(10, 26, 47, 0.92);
  background: rgba(255, 255, 255, 0.92);
  border-radius: 1rem;
  border: 1px solid rgba(10, 26, 47, 0.12);
  overflow: hidden;
  box-shadow: 0 18px 44px rgba(10, 26, 47, 0.06);
}

.client-detail-panel__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
}

.client-detail-panel__empty-icon {
  width: 5rem;
  height: 5rem;
  color: rgba(212, 175, 55, 0.35);
}

.client-detail-panel__empty-title {
  font-size: var(--text-lg);
  font-weight: var(--font-weight-semibold);
  color: rgba(10, 26, 47, 0.86);
  margin: 0;
}

.client-detail-panel__empty-text {
  font-size: var(--text-sm);
  color: var(--bank-gray-500);
  max-width: 22rem;
  margin: 0;
  line-height: 1.5;
}

.client-detail-panel__scroll {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (min-width: 640px) {
  .client-detail-panel__scroll {
    padding: 1.25rem 1.35rem;
  }
}

.client-detail-card {
  border-radius: 0.875rem;
  border: 1px solid rgba(10, 26, 47, 0.1);
  overflow: hidden;
  background: rgba(10, 26, 47, 0.02);
  transition: box-shadow 0.2s ease;
}

.client-detail-card:hover {
  box-shadow: 0 14px 34px rgba(10, 26, 47, 0.08);
}

.client-detail-card__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(10, 26, 47, 0.08);
  background: rgba(255, 255, 255, 0.85);
}

.client-detail-card__icon {
  width: 2rem;
  height: 2rem;
  padding: 0.35rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
}

.client-detail-card__icon--primary {
  background: rgba(212, 175, 55, 0.14);
  color: rgba(10, 26, 47, 0.88);
  border: 1px solid rgba(212, 175, 55, 0.22);
}

.client-detail-card__icon--navy {
  background: rgba(10, 26, 47, 0.08);
  color: rgba(10, 26, 47, 0.88);
  border: 1px solid rgba(10, 26, 47, 0.12);
}

.client-detail-card__icon--gold {
  background: rgba(212, 175, 55, 0.12);
  color: rgba(10, 26, 47, 0.86);
  border: 1px solid rgba(212, 175, 55, 0.22);
}

.client-detail-card__icon--success {
  background: rgba(45, 134, 89, 0.12);
  color: var(--bank-success);
  border: 1px solid rgba(45, 134, 89, 0.22);
}

.client-detail-card__title {
  flex: 1;
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  color: rgba(10, 26, 47, 0.9);
}

.client-detail-card__link {
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  color: rgba(212, 175, 55, 0.95);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.15rem 0;
}

.client-detail-card__link:hover {
  text-decoration: underline;
  color: rgba(10, 26, 47, 0.92);
}

.client-detail-card__body {
  padding: 0.75rem 1rem;
}

.client-detail-card__grid {
  display: grid;
  gap: 0.5rem;
}

.client-detail-card__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0 0.5rem;
  align-items: baseline;
}

.client-detail-card__row dt {
  font-size: var(--text-xs);
  color: var(--bank-gray-500);
  width: 6rem;
  flex-shrink: 0;
}

.client-detail-card__row dd {
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: rgba(10, 26, 47, 0.92);
  margin: 0;
}

.client-detail-card__doc-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem 0.75rem;
  align-items: center;
  margin-top: 0.75rem;
}

.client-detail-card__doc-label {
  font-size: var(--text-xs);
  color: var(--bank-gray-500);
}

.client-detail-panel__badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.45rem;
  border-radius: 0.375rem;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
}

.client-detail-panel__badge--primary {
  background: rgba(212, 175, 55, 0.14);
  color: rgba(10, 26, 47, 0.88);
  border: 1px solid rgba(212, 175, 55, 0.28);
}

.client-detail-panel__badge--success {
  background: rgba(45, 134, 89, 0.12);
  color: var(--bank-success);
  border: 1px solid rgba(45, 134, 89, 0.22);
}

.client-detail-panel__badge--warning {
  background: rgba(212, 175, 55, 0.12);
  color: rgba(10, 26, 47, 0.82);
  border: 1px solid rgba(212, 175, 55, 0.22);
}

.client-detail-panel__badge--neutral {
  background: rgba(10, 26, 47, 0.05);
  color: rgba(10, 26, 47, 0.62);
  border: 1px solid rgba(10, 26, 47, 0.1);
}

.client-detail-panel__badge--danger {
  background: rgba(197, 48, 48, 0.1);
  color: var(--bank-danger);
  border: 1px solid rgba(197, 48, 48, 0.2);
}

.client-detail-panel__doc-dot {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 999px;
}

.client-detail-panel__doc-dot--ok {
  background: var(--bank-success);
  box-shadow: 0 0 0 2px rgba(45, 134, 89, 0.2);
}

.client-detail-panel__doc-dot--missing {
  background: rgba(10, 26, 47, 0.12);
}

.client-detail-panel__log-list,
.client-detail-panel__sig-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.client-detail-panel__log-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid rgba(10, 26, 47, 0.06);
}

.client-detail-panel__log-item:last-child {
  border-bottom: none;
}

.client-detail-panel__log-subject {
  font-size: var(--text-sm);
  color: rgba(10, 26, 47, 0.86);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.client-detail-panel__log-date {
  font-size: var(--text-xs);
  color: var(--bank-gray-400);
  flex-shrink: 0;
}

.client-detail-panel__sig-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid rgba(10, 26, 47, 0.06);
}

.client-detail-panel__sig-item:last-child {
  border-bottom: none;
}

.client-detail-panel__sig-doc {
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: rgba(10, 26, 47, 0.88);
  flex: 1;
  min-width: 0;
}

.client-detail-panel__sig-date {
  font-size: var(--text-xs);
  color: var(--bank-gray-400);
}

.client-detail-panel__muted {
  font-size: var(--text-sm);
  color: var(--bank-gray-500);
  margin: 0;
}
</style>
